import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, MessageCircle, ExternalLink, Flame, ChevronLeft, ChevronRight } from "lucide-react";

export function BoostedBanner({ placement = "feed" }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);

  const ROTATION_MS = 5000;

  const fetchBoosted = useCallback(async () => {
    try {
      const res = await api.get("/posts/boosted/active");
      setPosts(res.data.posts || []);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => { fetchBoosted(); }, [fetchBoosted]);

  useEffect(() => {
    if (posts.length <= 1) return;
    setProgress(0);
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgress(Math.min((elapsed / ROTATION_MS) * 100, 100));
    }, 50);
    timerRef.current = setTimeout(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % posts.length);
    }, ROTATION_MS);
    return () => { clearTimeout(timerRef.current); clearInterval(progressInterval); };
  }, [currentIndex, posts.length]);

  const goTo = (i) => {
    clearTimeout(timerRef.current);
    setDirection(i > currentIndex ? 1 : -1);
    setCurrentIndex(i);
  };
  const goPrev = () => {
    clearTimeout(timerRef.current);
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + posts.length) % posts.length);
  };
  const goNext = () => {
    clearTimeout(timerRef.current);
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % posts.length);
  };

  if (!posts.length) return null;
  const post = posts[currentIndex];

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 120 : -120, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -120 : 120, opacity: 0 }),
  };

  if (placement === "sidebar") {
    return (
      <div className="bg-gradient-to-br from-[#FF6B35]/5 to-[#7000FF]/5 border border-[#FF6B35]/20 rounded-2xl p-4 space-y-3" data-testid="boosted-sidebar">
        <div className="flex items-center gap-1.5">
          <Flame className="w-3 h-3 text-[#FF6B35]" />
          <span className="text-[9px] font-bold text-[#FF6B35] uppercase tracking-widest">En vedette</span>
        </div>
        {posts.slice(0, 3).map((p) => (
          <div
            key={p.id}
            onClick={() => navigate(`/artist/${p.artist_id}`)}
            className="bg-card border border-border rounded-xl p-3 cursor-pointer hover:border-[#FF6B35]/40 transition-all group"
            data-testid={`sidebar-ad-${p.id}`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center overflow-hidden">
                {p.artist_avatar ? (
                  <img src={p.artist_avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-[8px] font-bold">{(p.artist_name || "?")[0]}</span>
                )}
              </div>
              <span className="text-[10px] font-bold text-card-foreground group-hover:text-[#FF6B35] transition-colors truncate">{p.artist_name}</span>
            </div>
            <p className="text-[11px] text-card-foreground/80 line-clamp-2 leading-relaxed">{p.content?.substring(0, 60)}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-3xl shadow-lg" data-testid="boosted-banner-carousel">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1A1230] via-[#2A1B45] to-[#0F0A1F]" />
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIvPjwvc3ZnPg==')] opacity-50" />
      <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#FF6B35]/15 rounded-full blur-3xl" />
      <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#7000FF]/15 rounded-full blur-3xl" />

      <div className="relative p-5 sm:p-6">
        {/* Header: label + dots + arrows */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-[#FF6B35]/20 to-[#FF8FA3]/10 backdrop-blur-sm rounded-full px-3 py-1 border border-[#FF6B35]/20">
            <Flame className="w-3 h-3 text-[#FF6B35]" />
            <span className="text-[10px] font-bold text-[#FF6B35] uppercase tracking-wider">En vedette</span>
          </div>

          {posts.length > 1 && (
            <div className="flex items-center gap-2">
              <button onClick={(e) => { e.stopPropagation(); goPrev(); }} className="w-6 h-6 rounded-full bg-foreground/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="banner-prev">
                <ChevronLeft className="w-3.5 h-3.5 text-white/70" />
              </button>
              <div className="flex gap-1.5">
                {posts.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); goTo(i); }}
                    className="relative h-1.5 rounded-full overflow-hidden transition-all"
                    style={{ width: i === currentIndex ? 24 : 6, backgroundColor: i === currentIndex ? 'transparent' : 'rgba(255,255,255,0.2)' }}
                    data-testid={`banner-dot-${i}`}
                  >
                    {i === currentIndex && (
                      <>
                        <div className="absolute inset-0 bg-white/20 rounded-full" />
                        <div className="absolute inset-0 bg-[#FF6B35] rounded-full origin-left" style={{ transform: `scaleX(${progress / 100})` }} />
                      </>
                    )}
                  </button>
                ))}
              </div>
              <button onClick={(e) => { e.stopPropagation(); goNext(); }} className="w-6 h-6 rounded-full bg-foreground/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="banner-next">
                <ChevronRight className="w-3.5 h-3.5 text-white/70" />
              </button>
            </div>
          )}
        </div>

        {/* Sliding content */}
        <AnimatePresence custom={direction} mode="wait">
          <motion.div
            key={post.id}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="flex items-start gap-4 cursor-pointer"
            onClick={() => navigate(`/artist/${post.artist_id}`)}
            data-testid={`boosted-banner-${post.id}`}
          >
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0 overflow-hidden shadow-lg shadow-[#FF6B35]/20 group-hover:shadow-[#FF6B35]/40 transition-all duration-300">
              {post.artist_avatar ? (
                <img src={post.artist_avatar} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-heading font-bold text-2xl">{(post.artist_name || "?")[0].toUpperCase()}</span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-base sm:text-lg text-white hover:text-[#FF6B35] transition-colors">
                {post.artist_name || "Artiste"}
              </p>
              <p className="text-sm text-white/70 mt-1 line-clamp-2 leading-relaxed">
                {post.content || ""}
              </p>
              <div className="flex items-center gap-4 mt-3">
                <span className="flex items-center gap-1.5 text-xs text-white/50">
                  <Heart className="w-3.5 h-3.5" /> {post.likes_count || 0}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-white/50">
                  <MessageCircle className="w-3.5 h-3.5" /> {post.comments_count || 0}
                </span>
                <span className="ml-auto flex items-center gap-1.5 text-xs text-[#FF6B35] font-medium hover:underline">
                  Voir le profil <ExternalLink className="w-3 h-3" />
                </span>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Counter */}
        {posts.length > 1 && (
          <div className="mt-3 text-center">
            <span className="text-[10px] text-white/30">{currentIndex + 1} / {posts.length}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function BoostedBannerInline({ post }) {
  const navigate = useNavigate();

  if (!post?.is_boosted) return null;

  return (
    <div
      className="relative overflow-hidden rounded-3xl shadow-lg cursor-pointer group"
      onClick={() => navigate(`/artist/${post.artist_id}`)}
      data-testid={`inline-ad-${post.id}`}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-[#1A1230] via-[#2A1B45] to-[#1A1230]" />
      <div className="absolute -top-10 right-10 w-32 h-32 bg-[#FF6B35]/10 rounded-full blur-2xl" />

      <div className="relative p-4 flex items-center gap-4">
        <div className="flex items-center gap-1.5 bg-[#FF6B35]/20 rounded-full px-2.5 py-1 shrink-0">
          <Flame className="w-3 h-3 text-[#FF6B35]" />
          <span className="text-[8px] font-bold text-[#FF6B35] uppercase tracking-wider">En vedette</span>
        </div>

        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center shrink-0 overflow-hidden">
          {post.artist_avatar ? (
            <img src={post.artist_avatar} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-white font-bold text-sm">{(post.artist_name || "?")[0]}</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-sm text-white truncate">{post.artist_name}</p>
          <p className="text-xs text-white/60 truncate">{post.content?.substring(0, 50)}</p>
        </div>

        <span className="text-xs text-[#FF6B35] font-medium shrink-0 group-hover:underline flex items-center gap-1">
          Voir <ExternalLink className="w-3 h-3" />
        </span>
      </div>
    </div>
  );
}
